export interface RWQuestion {
  id: number;
  passage: string;
  question: string;
  choices: string[];
  correctAnswer: number;
  explanation: string;
  subtopic: string;
}

export const expressionIdeasQuestions: RWQuestion[] = [
  {
    id: 1,
    passage: "The research team conducted experiments over a six-month period. The results were analyzed using statistical software. The findings were published in a peer-reviewed journal.",
    question: "Which choice most effectively combines the sentences?",
    choices: [
      "period, the results were analyzed using statistical software, and the findings were published",
      "period; analyzing the results using statistical software and publishing the findings",
      "period, analyzed the results using statistical software, and published the findings",
      "period and the results were analyzed using statistical software and the findings were published"
    ],
    correctAnswer: 2,
    explanation: "Choice C creates parallel structure with three verb phrases sharing the same subject ('The research team'): conducted, analyzed, and published. This is the most concise and grammatically correct combination.",
    subtopic: "Transitions"
  },
  {
    id: 2,
    passage: "Solar panels convert sunlight into electricity. ______ they require significant upfront investment, their long-term cost savings make them economically viable for many homeowners.",
    question: "Which choice completes the text with the most logical transition?",
    choices: [
      "Because",
      "Although",
      "Therefore",
      "Similarly"
    ],
    correctAnswer: 1,
    explanation: "'Although' is correct because it introduces a contrast between the disadvantage (upfront cost) and the advantage (long-term savings). The sentence presents two opposing ideas that need a contrasting transition.",
    subtopic: "Transitions"
  },
  {
    id: 3,
    passage: "The ancient city of Petra was carved directly into rose-colored rock cliffs. ______ its remote location in the Jordanian desert helped protect it from invaders for centuries.",
    question: "Which choice completes the text with the most logical transition?",
    choices: [
      "However,",
      "For example,",
      "Additionally,",
      "In contrast,"
    ],
    correctAnswer: 2,
    explanation: "'Additionally' is correct because the second sentence provides another complementary fact about Petra. Both sentences describe features of the city, so an additive transition is appropriate.",
    subtopic: "Transitions"
  },
  {
    id: 4,
    passage: "Marie Curie discovered radium in 1898. She became the first woman to win a Nobel Prize. She remains the only person to win Nobel Prizes in two different sciences.",
    question: "Which choice most effectively combines the sentences?",
    choices: [
      "Marie Curie discovered radium in 1898, became the first woman to win a Nobel Prize, and remains the only person to win Nobel Prizes in two different sciences.",
      "Marie Curie, who discovered radium in 1898 and became the first woman to win a Nobel Prize, remains the only person to win Nobel Prizes in two different sciences.",
      "Discovering radium in 1898, Marie Curie became the first woman to win a Nobel Prize and remains the only person to win Nobel Prizes in two different sciences.",
      "Marie Curie discovered radium in 1898; she became the first woman to win a Nobel Prize; she remains the only person to win Nobel Prizes in two different sciences."
    ],
    correctAnswer: 1,
    explanation: "Choice B uses a relative clause to subordinate the first two facts while emphasizing the most significant achievement in the main clause. This creates the most sophisticated and effective sentence structure.",
    subtopic: "Transitions"
  },
  {
    id: 5,
    passage: "The committee reviewed the proposal carefully. ______ they identified several areas requiring revision before final approval could be granted.",
    question: "Which choice completes the text with the most logical transition?",
    choices: [
      "Nevertheless,",
      "Consequently,",
      "For instance,",
      "Meanwhile,"
    ],
    correctAnswer: 1,
    explanation: "'Consequently' is correct because identifying areas for revision is a logical result of careful review. The transition shows cause and effect between the two clauses.",
    subtopic: "Transitions"
  },
  {
    id: 6,
    passage: "The company implemented new safety protocols. Employee injury rates decreased by 40%. Worker satisfaction scores improved significantly.",
    question: "Which choice most effectively uses a transition to combine the sentences?",
    choices: [
      "The company implemented new safety protocols, and employee injury rates decreased by 40%, and worker satisfaction scores improved significantly.",
      "The company implemented new safety protocols; consequently, employee injury rates decreased by 40% and worker satisfaction scores improved significantly.",
      "The company implemented new safety protocols, but employee injury rates decreased by 40% and worker satisfaction scores improved significantly.",
      "The company implemented new safety protocols, employee injury rates decreased by 40%, worker satisfaction scores improved significantly."
    ],
    correctAnswer: 1,
    explanation: "Choice B uses a semicolon and 'consequently' to show that the improvements were results of the new protocols. This creates a clear cause-and-effect relationship between the implementation and the outcomes.",
    subtopic: "Transitions"
  },
  {
    id: 7,
    passage: "Photosynthesis is the process by which plants convert sunlight into chemical energy. ______ cellular respiration is the process by which organisms break down glucose to release energy.",
    question: "Which choice completes the text with the most logical transition?",
    choices: [
      "Similarly,",
      "Conversely,",
      "Therefore,",
      "Specifically,"
    ],
    correctAnswer: 1,
    explanation: "'Conversely' is correct because cellular respiration is essentially the opposite process of photosynthesis—one builds glucose using energy, the other breaks down glucose to release energy. The transition signals this contrast.",
    subtopic: "Transitions"
  },
  {
    id: 8,
    passage: "Beethoven composed his Ninth Symphony while completely deaf. ______ he never heard the piece performed, it remains one of the most celebrated works in classical music.",
    question: "Which choice completes the text with the most logical transition?",
    choices: [
      "Because",
      "Although",
      "Since",
      "When"
    ],
    correctAnswer: 1,
    explanation: "'Although' is correct because it introduces a contrast between the unfortunate fact that Beethoven never heard the symphony and the positive fact that it is celebrated. The transition shows these ideas are in tension.",
    subtopic: "Transitions"
  },
  {
    id: 9,
    passage: "The playwright's work challenges conventional narrative structures. ______ her latest production abandons linear storytelling entirely, presenting scenes in reverse chronological order.",
    question: "Which choice completes the text with the most logical transition?",
    choices: [
      "Nevertheless,",
      "In fact,",
      "However,",
      "On the other hand,"
    ],
    correctAnswer: 1,
    explanation: "'In fact' is correct because the second sentence provides a specific example that reinforces and intensifies the claim made in the first sentence. The transition signals that what follows is evidence or elaboration.",
    subtopic: "Transitions"
  },
  {
    id: 10,
    passage: "The invention of the printing press revolutionized information dissemination in Europe. ______ literacy rates increased dramatically, and ideas spread more rapidly than ever before.",
    question: "Which choice completes the text with the most logical transition?",
    choices: [
      "For example,",
      "Nevertheless,",
      "Subsequently,",
      "In contrast,"
    ],
    correctAnswer: 2,
    explanation: "'Subsequently' is correct because the increased literacy and rapid spread of ideas happened as a result of and after the printing press revolution. The transition shows temporal and causal sequence.",
    subtopic: "Transitions"
  },
  {
    id: 11,
    passage: "The ancient Romans developed sophisticated engineering techniques. They built aqueducts to transport water across vast distances. These structures still stand today.",
    question: "Which choice most effectively combines the sentences?",
    choices: [
      "The ancient Romans developed sophisticated engineering techniques and built aqueducts to transport water across vast distances and these structures still stand today.",
      "The ancient Romans developed sophisticated engineering techniques, building aqueducts to transport water across vast distances, structures that still stand today.",
      "The ancient Romans developed sophisticated engineering techniques; they built aqueducts to transport water across vast distances; these structures still stand today.",
      "Developing sophisticated engineering techniques, the ancient Romans built aqueducts to transport water across vast distances, and these structures still stand today."
    ],
    correctAnswer: 1,
    explanation: "Choice B uses a participial phrase and an appositive to create a sophisticated, flowing sentence that emphasizes the main idea while subordinating supporting details.",
    subtopic: "Transitions"
  },
  {
    id: 12,
    passage: "A student is writing a report on climate change and wants to emphasize the urgency of the problem. The student has gathered the following notes:\n• Global average temperatures have risen 1.1°C since pre-industrial times.\n• The last decade was the hottest on record.\n• Scientists warn that exceeding 1.5°C of warming could trigger irreversible tipping points.\n• Renewable energy adoption has accelerated in recent years.",
    question: "Which choice most effectively uses the notes to emphasize the urgency of climate change?",
    choices: [
      "Renewable energy adoption has accelerated in recent years, suggesting progress on climate change.",
      "Global average temperatures have risen 1.1°C, and the last decade was the hottest on record.",
      "With global temperatures already 1.1°C above pre-industrial levels and scientists warning that just 0.4°C more could trigger irreversible tipping points, the urgency of climate action cannot be overstated.",
      "Scientists have been studying climate change for decades and have gathered significant data on temperature trends."
    ],
    correctAnswer: 2,
    explanation: "Choice C most effectively conveys urgency by combining the temperature data with the warning about tipping points, creating a sense of how close we are to a dangerous threshold. It directly addresses the student's goal of emphasizing urgency.",
    subtopic: "Rhetorical Synthesis"
  },
  {
    id: 13,
    passage: "A researcher is writing about the benefits of sleep and wants to support the claim that sleep deprivation has serious health consequences. The researcher has the following notes:\n• Adults who sleep fewer than 7 hours per night are 3 times more likely to develop a cold.\n• Chronic sleep deprivation is linked to increased risk of heart disease, diabetes, and obesity.\n• Sleep allows the brain to clear toxic proteins associated with Alzheimer's disease.\n• Most adults report not getting enough sleep on a regular basis.",
    question: "Which choice most effectively uses the notes to support the claim about serious health consequences?",
    choices: [
      "Most adults report not getting enough sleep on a regular basis, which is a widespread problem.",
      "Sleep allows the brain to clear toxic proteins, which is one benefit of adequate rest.",
      "Chronic sleep deprivation is linked to heart disease, diabetes, and obesity, and adults who sleep fewer than 7 hours are 3 times more likely to develop a cold, demonstrating the serious physical toll of insufficient sleep.",
      "Researchers have studied sleep patterns for many years and continue to learn about its importance."
    ],
    correctAnswer: 2,
    explanation: "Choice C directly supports the claim about serious health consequences by citing two specific, concrete pieces of evidence: the link to major diseases and the increased susceptibility to illness. It is the most focused and evidence-rich option.",
    subtopic: "Rhetorical Synthesis"
  },
  {
    id: 14,
    passage: "A student is writing an essay arguing that public libraries are essential community institutions. The student wants to conclude the essay by reinforcing the central argument. The student has gathered these notes:\n• Libraries provide free internet access to people who cannot afford it at home.\n• Libraries offer literacy programs for children and adults.\n• Libraries serve as community gathering spaces and provide social services.\n• Some cities have reduced library funding in recent years.",
    question: "Which choice most effectively concludes the essay by reinforcing the argument that libraries are essential?",
    choices: [
      "Some cities have reduced library funding in recent years, which is a concerning trend.",
      "Libraries offer many different programs and services to community members.",
      "By providing free internet access, literacy programs, and vital social services, public libraries serve as irreplaceable equalizers—ensuring that education and community resources are available to everyone, regardless of income.",
      "Libraries have existed for thousands of years and continue to evolve with changing community needs."
    ],
    correctAnswer: 2,
    explanation: "Choice C most effectively reinforces the central argument by synthesizing three key pieces of evidence into a conclusion that emphasizes the library's role as an 'equalizer'—directly supporting the claim that libraries are essential community institutions.",
    subtopic: "Rhetorical Synthesis"
  },
  {
    id: 15,
    passage: "The novelist's latest work explores themes of identity and belonging. ______ it has received critical acclaim for its nuanced portrayal of immigrant experiences.",
    question: "Which choice completes the text with the most logical transition?",
    choices: [
      "In other words,",
      "On the contrary,",
      "As a result,",
      "For this reason,"
    ],
    correctAnswer: 2,
    explanation: "'As a result' is correct because the critical acclaim is a consequence of the novel's exploration of themes. The transition shows that the quality of the work led to the positive reception.",
    subtopic: "Transitions"
  },
  {
    id: 16,
    passage: "The study's findings suggest that regular exercise improves not only physical health but also mental well-being and cognitive function. ______ researchers recommend that adults engage in at least 150 minutes of moderate exercise per week.",
    question: "Which choice completes the text with the most logical transition?",
    choices: [
      "However,",
      "In contrast,",
      "Based on these findings,",
      "Despite this,"
    ],
    correctAnswer: 2,
    explanation: "'Based on these findings' is correct because the recommendation logically follows from the research results. The transition explicitly connects the evidence to the conclusion drawn from it.",
    subtopic: "Transitions"
  },
  {
    id: 17,
    passage: "A writer is drafting an introduction to an article about the decline of honeybee populations. The writer wants to open with a statement that establishes the significance of the problem. The writer has these notes:\n• Honeybees pollinate approximately one-third of the food humans eat.\n• Bee populations have declined by nearly 30% in recent decades.\n• Pesticides, habitat loss, and disease are primary causes of decline.\n• Some regions have reported complete colony collapse.",
    question: "Which choice most effectively opens the article by establishing the significance of the problem?",
    choices: [
      "Pesticides, habitat loss, and disease are among the primary causes of honeybee decline.",
      "Some regions have reported complete colony collapse among honeybee populations.",
      "Since honeybees pollinate roughly one-third of the food we eat, the nearly 30% decline in their populations over recent decades is not merely an ecological concern—it is a direct threat to the global food supply.",
      "Honeybee populations have been studied by scientists for many years, and researchers continue to investigate the causes of their decline."
    ],
    correctAnswer: 2,
    explanation: "Choice C establishes significance most effectively by immediately connecting the bee decline to its impact on the human food supply. It uses the most compelling combination of facts and frames the issue as urgent and personally relevant to readers.",
    subtopic: "Rhetorical Synthesis"
  },
  {
    id: 18,
    passage: "The city council approved the new transit plan. ______ construction on the expanded subway line is expected to begin next spring.",
    question: "Which choice completes the text with the most logical transition?",
    choices: [
      "Nevertheless,",
      "In contrast,",
      "Following this approval,",
      "Despite this,"
    ],
    correctAnswer: 2,
    explanation: "'Following this approval' is correct because it shows that the construction is a direct consequence of the council's approval. The transition establishes a clear temporal and causal sequence.",
    subtopic: "Transitions"
  },
  {
    id: 19,
    passage: "The documentary examines three distinct approaches to urban farming. ______ it profiles rooftop gardens in New York City, vertical farms in Singapore, and community plots in Detroit.",
    question: "Which choice completes the text with the most logical transition?",
    choices: [
      "However,",
      "Specifically,",
      "Therefore,",
      "Although,"
    ],
    correctAnswer: 1,
    explanation: "'Specifically' is correct because the second sentence provides concrete examples that illustrate the general claim in the first sentence. The transition signals that specific details will follow.",
    subtopic: "Transitions"
  },
  {
    id: 20,
    passage: "A student is writing a persuasive essay arguing that schools should start later in the morning. The student wants to include a sentence that addresses a potential counterargument. The student has these notes:\n• Teenagers' circadian rhythms naturally shift later, making early rising difficult.\n• Studies show later start times improve grades and reduce absenteeism.\n• Some parents worry that later start times would conflict with work schedules.\n• Schools that shifted to later start times reported improved student mental health.",
    question: "Which choice most effectively addresses the counterargument about parents' work schedules?",
    choices: [
      "Teenagers' circadian rhythms naturally shift later, making early rising biologically difficult for them.",
      "Schools that shifted to later start times reported improved student mental health outcomes.",
      "While some parents worry that later start times would conflict with work schedules, the documented improvements in student grades, attendance, and mental health suggest that the benefits to children outweigh the logistical challenges for families.",
      "Studies consistently show that later school start times improve academic performance and reduce absenteeism among students."
    ],
    correctAnswer: 2,
    explanation: "Choice C directly acknowledges the counterargument ('While some parents worry...') and then refutes it with evidence. This is the most rhetorically effective approach to addressing an opposing view in a persuasive essay.",
    subtopic: "Rhetorical Synthesis"
  },
  {
    id: 21,
    passage: "The museum's collection spans five centuries of European art. ______ it includes masterworks by Rembrandt, Vermeer, and Caravaggio alongside lesser-known artists who shaped the same era.",
    question: "Which choice completes the text with the most logical transition?",
    choices: [
      "In contrast,",
      "Notably,",
      "However,",
      "As a result,"
    ],
    correctAnswer: 1,
    explanation: "'Notably' is correct because it introduces specific, impressive examples that illustrate the breadth of the collection described in the first sentence. It signals that what follows is particularly worth highlighting.",
    subtopic: "Transitions"
  },
  {
    id: 22,
    passage: "The new medication showed promising results in early trials. ______ researchers caution that larger studies are needed before it can be approved for widespread use.",
    question: "Which choice completes the text with the most logical transition?",
    choices: [
      "Similarly,",
      "As a result,",
      "Nevertheless,",
      "Furthermore,"
    ],
    correctAnswer: 2,
    explanation: "'Nevertheless' is correct because it introduces a contrasting note of caution despite the promising results. The transition signals that the second sentence qualifies or complicates the positive news in the first.",
    subtopic: "Transitions"
  },
  {
    id: 23,
    passage: "A journalist is writing a profile of a pioneering architect and wants to open with a sentence that captures the architect's significance. The journalist has these notes:\n• The architect designed over 200 buildings across 30 countries.\n• Her work is credited with popularizing sustainable building practices.\n• She was the first woman to win the Pritzker Architecture Prize.\n• Her designs blend traditional cultural motifs with modernist forms.",
    question: "Which choice most effectively opens the profile by capturing the architect's significance?",
    choices: [
      "The architect designed over 200 buildings across 30 countries during her career.",
      "Her designs blend traditional cultural motifs with modernist architectural forms.",
      "As the first woman to win the Pritzker Architecture Prize and the designer credited with bringing sustainable building practices into the mainstream, she transformed not just skylines but the entire philosophy of how buildings should relate to their environment.",
      "The architect worked in 30 different countries throughout her long and distinguished career."
    ],
    correctAnswer: 2,
    explanation: "Choice C most effectively captures significance by combining her historic achievement (first woman to win the Pritzker Prize) with her broader impact (transforming sustainable building practices) into a compelling opening statement.",
    subtopic: "Rhetorical Synthesis"
  },
  {
    id: 24,
    passage: "The expedition team reached the summit after three weeks of climbing. ______ they had endured blizzards, altitude sickness, and equipment failures along the way.",
    question: "Which choice completes the text with the most logical transition?",
    choices: [
      "Therefore,",
      "In addition,",
      "Remarkably,",
      "Similarly,"
    ],
    correctAnswer: 2,
    explanation: "'Remarkably' is correct because it signals that the achievement is impressive given the obstacles described. The transition conveys admiration and emphasizes the difficulty of what was accomplished.",
    subtopic: "Transitions"
  },
  {
    id: 25,
    passage: "The economic data from the past decade tells a complex story. ______ GDP growth has been strong, but wage growth for middle- and lower-income workers has lagged significantly behind corporate profits.",
    question: "Which choice completes the text with the most logical transition?",
    choices: [
      "Consequently,",
      "On one hand,",
      "In summary,",
      "For instance,"
    ],
    correctAnswer: 1,
    explanation: "'On one hand' is correct because it introduces the first part of a contrast—strong GDP growth—which will be followed by the contrasting reality of lagging wages. It signals that a two-sided picture is being presented.",
    subtopic: "Transitions"
  },
  {
    id: 26,
    passage: "A student is writing a report on the history of the internet and wants to include a sentence explaining why the invention of the World Wide Web was a turning point. The student has these notes:\n• The internet existed as a military and academic network before the Web.\n• Tim Berners-Lee invented the World Wide Web in 1989.\n• The Web made the internet accessible to non-technical users through browsers.\n• By 1995, millions of ordinary people were using the Web for the first time.",
    question: "Which choice most effectively explains why the World Wide Web was a turning point?",
    choices: [
      "Tim Berners-Lee invented the World Wide Web in 1989, which was an important development.",
      "By 1995, millions of ordinary people were using the Web for the first time.",
      "Before the Web, the internet was limited to military and academic users; Berners-Lee's invention of the Web transformed it into a tool accessible to anyone with a browser, fundamentally democratizing access to information.",
      "The internet has a long history that predates the World Wide Web by several decades."
    ],
    correctAnswer: 2,
    explanation: "Choice C most effectively explains the turning point by contrasting the pre-Web internet (limited to specialists) with the post-Web internet (accessible to everyone), directly addressing why the invention was transformative.",
    subtopic: "Rhetorical Synthesis"
  },
  {
    id: 27,
    passage: "The new urban planning initiative has received praise from environmental groups. ______ some longtime residents have expressed concerns about rising property values and displacement.",
    question: "Which choice completes the text with the most logical transition?",
    choices: [
      "Furthermore,",
      "As a result,",
      "However,",
      "Similarly,"
    ],
    correctAnswer: 2,
    explanation: "'However' is correct because it introduces a contrasting perspective—residents' concerns—after the positive reception from environmental groups. The transition signals a shift from one viewpoint to an opposing one.",
    subtopic: "Transitions"
  },
  {
    id: 28,
    passage: "The scientist presented her hypothesis at the conference. ______ she outlined the experimental design, described the preliminary data, and proposed next steps for further investigation.",
    question: "Which choice completes the text with the most logical transition?",
    choices: [
      "In contrast,",
      "In her presentation,",
      "Nevertheless,",
      "As a result,"
    ],
    correctAnswer: 1,
    explanation: "'In her presentation' is correct because it provides a natural bridge that specifies the context in which the actions described took place. It connects the general statement about presenting with the specific details that follow.",
    subtopic: "Transitions"
  },
  {
    id: 29,
    passage: "A writer is drafting an essay arguing that arts education should be preserved in schools. The writer wants to include a sentence that synthesizes research findings to support the argument. The writer has these notes:\n• Students in arts programs score higher on standardized tests in math and reading.\n• Arts education develops creativity and problem-solving skills valued by employers.\n• Schools that cut arts programs see increased student disengagement.\n• Arts participation is linked to lower dropout rates.",
    question: "Which choice most effectively synthesizes the research to support the argument?",
    choices: [
      "Arts education has been part of school curricula for many decades.",
      "Some schools have cut arts programs due to budget constraints.",
      "Research consistently shows that arts education improves academic performance, reduces dropout rates, and develops the creative problem-solving skills that employers increasingly demand—making it not a luxury but a core component of effective education.",
      "Students who participate in arts programs tend to score higher on standardized tests."
    ],
    correctAnswer: 2,
    explanation: "Choice C synthesizes multiple research findings (academic performance, dropout rates, employer-valued skills) into a single, compelling argument that directly supports the claim that arts education is essential, not optional.",
    subtopic: "Rhetorical Synthesis"
  },
  {
    id: 30,
    passage: "The first chapter of the novel establishes the protagonist's ordinary life in meticulous detail. ______ the arrival of a mysterious letter in chapter two shatters this carefully constructed normalcy.",
    question: "Which choice completes the text with the most logical transition?",
    choices: [
      "Similarly,",
      "For this reason,",
      "By contrast,",
      "In addition,"
    ],
    correctAnswer: 2,
    explanation: "'By contrast' is correct because it signals the shift from the ordinary life established in chapter one to the disruption caused by the mysterious letter in chapter two. The transition highlights the contrast between the two chapters.",
    subtopic: "Transitions"
  }
];
